import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ApiError,
  getUserFacingApiMessage,
  getLocalUserFacingApiMessage,
  passThroughMessage,
} from "@/lib/api-client";
import { Sentry } from "@/lib/sentry";
import { createOrderErrorMessage } from "@/features/marketplace/lib/order-errors";
import i18n from "@/lib/i18n";
import {
  fetchVendors,
  fetchVendorDetails,
  fetchConnections,
  createConnection,
  updateAnswers,
  resignConnection,
  disconnectVendor,
  fetchOrders,
  fetchOrder,
  fetchSentScripts,
  fetchOrderHandoffDefaults,
  previewOrderHandoffEmail,
  createOrder,
  retryOrder,
  updateOrderEta,
  updateOrderFulfillment,
  fetchAdminOrders,
  updateOrderScriptFulfillment,
  fetchPartnerOrders,
  uploadPartnerPreflight,
  enablePartnerLogin,
  createOpportunityCustomBrief,
  fetchOrderScriptPreflightComments,
  fetchPartnerOrderScriptPreflightComments,
  fetchMarketplaceOrderScriptByScriptReviewId,
  addOrderScriptPreflightComment,
  addPartnerOrderScriptNote,
} from "@/features/marketplace/api/marketplace.api";
import type {
  CreateConnectionPayload,
  UpdateAnswersPayload,
  ResignConnectionPayload,
  CreateOrderPayload,
  OrderHandoffEmailPreviewPayload,
  MarketplaceFulfillmentStatus,
  MarketplaceOrder,
  AdminOrdersFilters,
  TenantOrdersFilters,
  TenantOrdersPageResponse,
  PartnerOrdersFilter,
  PartnerOrdersQueryFilters,
  UpdateOrderScriptFulfillmentPayload,
  CreateCustomBriefPayload,
  MarketplaceOrderScriptByReviewLink,
} from "@/features/marketplace/types/marketplace.types";
import {
  fetchAdminVendors,
  fetchAdminVendor,
  createAdminVendor,
  replaceAdminVendor,
  activateAdminVendor,
  approveVendorContactEmail,
  deactivateAdminVendor,
  revokeVendorContactEmail,
  fetchPendingVendors,
  fetchPendingOrderHandoffs,
  linkPendingVendor,
  rejectPendingVendor,
  retryAdminOrderHandoff,
} from "@/features/admin/marketplace/api/admin-vendors.api";
import {
  fetchTeamRecipients,
  createTeamRecipient,
  updateTeamRecipient,
  deleteTeamRecipient,
} from "@/features/admin/marketplace/api/team-recipients.api";
import {
  acceptOrderImportWarnings,
  acceptPrefilledOrderImportDates,
  bulkSetOrderImportClientStartDates,
  bulkSetOrderImportNewVendorMapping,
  bulkSetOrderImportVendorMapping,
  commitOrderImport,
  fetchOrderImportPreview,
  parseOrderImportSpreadsheet,
  updateOrderImportRow,
} from "@/features/admin/marketplace/api/admin-order-import.api";
import type { CreativeImportPreviewDto } from "@nexus/contracts";
import { normalizeImportPreview } from "@/features/marketplace/admin/components/order-import/import-readiness";

import type { AdminVendorWritePayload } from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  TeamRecipientCreatePayload,
  TeamRecipientUpdatePayload,
} from "@/features/admin/marketplace/types/admin-marketplace.types";
import type {
  BulkSetClientStartDateBody,
  BulkSetVendorMappingBody,
  BulkSetNewVendorMappingBody,
  UpdateCreativeImportRowBody,
} from "@nexus/contracts";
import {
  hasInFlightHandoffOrders,
  ORDER_HANDOFF_POLL_INTERVAL_MS,
} from "@/features/marketplace/lib/order-handoff-ux";
import { opportunityKeys } from "./opportunities.queries";
import { personaOpportunityScriptKeys } from "./persona-opportunity-scripts.queries";

import { marketplaceKeys, MARKETPLACE_STALE_TIME_MS } from "./marketplace-query-keys";

function withNormalizedPreview(
  data: CreativeImportPreviewDto,
): CreativeImportPreviewDto {
  return normalizeImportPreview(data);
}

export function adminOrderImportQueryOptions(
  tenantId: string,
  importId: string | null,
) {
  return queryOptions({
    queryKey: marketplaceKeys.adminOrderImport(
      tenantId,
      importId ?? "__none__",
    ),
    queryFn: async () =>
      withNormalizedPreview(await fetchOrderImportPreview(tenantId, importId!)),
    enabled: Boolean(tenantId && importId),
    staleTime: MARKETPLACE_STALE_TIME_MS,
  });
}

export function useParseOrderImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { tenantId: string; file: File }) =>
      parseOrderImportSpreadsheet(args.tenantId, args.file),
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        marketplaceKeys.adminOrderImport(args.tenantId, data.id),
        withNormalizedPreview(data),
      );
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingVendors,
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingHandoffs,
      });
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useUpdateOrderImportRow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      tenantId: string;
      importId: string;
      rowId: string;
      payload: UpdateCreativeImportRowBody;
    }) =>
      updateOrderImportRow(
        args.tenantId,
        args.importId,
        args.rowId,
        args.payload,
      ),
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        marketplaceKeys.adminOrderImport(args.tenantId, data.id),
        withNormalizedPreview(data),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useBulkSetOrderImportClientStartDates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      tenantId: string;
      importId: string;
      payload: BulkSetClientStartDateBody;
    }) =>
      bulkSetOrderImportClientStartDates(
        args.tenantId,
        args.importId,
        args.payload,
      ),
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        marketplaceKeys.adminOrderImport(args.tenantId, data.id),
        withNormalizedPreview(data),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useBulkSetOrderImportVendorMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      tenantId: string;
      importId: string;
      payload: BulkSetVendorMappingBody;
    }) =>
      bulkSetOrderImportVendorMapping(
        args.tenantId,
        args.importId,
        args.payload,
      ),
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        marketplaceKeys.adminOrderImport(args.tenantId, data.id),
        withNormalizedPreview(data),
      );
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingVendors,
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingHandoffs,
      });
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useBulkSetOrderImportNewVendorMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      tenantId: string;
      importId: string;
      payload: BulkSetNewVendorMappingBody;
    }) =>
      bulkSetOrderImportNewVendorMapping(
        args.tenantId,
        args.importId,
        args.payload,
      ),
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        marketplaceKeys.adminOrderImport(args.tenantId, data.id),
        withNormalizedPreview(data),
      );
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingVendors,
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingHandoffs,
      });
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useAcceptPrefilledOrderImportDates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { tenantId: string; importId: string }) =>
      acceptPrefilledOrderImportDates(args.tenantId, args.importId),
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        marketplaceKeys.adminOrderImport(args.tenantId, data.id),
        withNormalizedPreview(data),
      );
      toast.success(
        i18n.t(
          "marketplace.admin.import.quickActions.acceptPrefilledDatesSuccess",
        ),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useAcceptOrderImportWarnings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { tenantId: string; importId: string }) =>
      acceptOrderImportWarnings(args.tenantId, args.importId),
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        marketplaceKeys.adminOrderImport(args.tenantId, data.id),
        withNormalizedPreview(data),
      );
      toast.success(
        i18n.t("marketplace.admin.import.quickActions.acceptWarningsSuccess"),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

export function useCommitOrderImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { tenantId: string; importId: string }) =>
      commitOrderImport(args.tenantId, args.importId),
    onSuccess: (data, args) => {
      queryClient.setQueryData(
        marketplaceKeys.adminOrderImport(args.tenantId, data.preview.id),
        withNormalizedPreview(data.preview),
      );
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.orders(args.tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingVendors,
      });
      queryClient.invalidateQueries({
        queryKey: marketplaceKeys.adminPendingHandoffs,
      });
      toast.success(
        data.frozenRowCount > 0
          ? i18n.t("marketplace.admin.import.commitSuccessWithFrozen", {
              batches: i18n.t("marketplace.admin.import.batchCount", {
                count: data.opportunitySetIds.length,
              }),
              scripts: i18n.t("marketplace.admin.import.scriptCount", {
                count: data.scriptCount,
              }),
              frozen: data.frozenRowCount,
              invites: i18n.t("marketplace.admin.import.inviteCount", {
                count: data.vendorInvitesSent,
              }),
            })
          : i18n.t("marketplace.admin.import.commitSuccess", {
              batches: i18n.t("marketplace.admin.import.batchCount", {
                count: data.opportunitySetIds.length,
              }),
              scripts: i18n.t("marketplace.admin.import.scriptCount", {
                count: data.scriptCount,
              }),
              invites: i18n.t("marketplace.admin.import.inviteCount", {
                count: data.vendorInvitesSent,
              }),
            }),
      );
    },
    onError: (err) => {
      toast.error(getUserFacingApiMessage(err));
    },
    meta: { skipGlobalError: true },
  });
}

// ─── Team notification recipients ───────────────────────────────────────────

